import { lazy, useEffect, type ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { DesktopOnly } from '@/components/DesktopOnly';
import { LoadingBlock } from '@/components/PageHeader';
import { LEAVE_PLAN_EXTERNAL_URL } from '@/lib/menu/menuConfig';
import { useExternalLinks } from '@/lib/settings/useExternalLinks';
import { HomePage } from '@/pages/home/HomePage';
import { MyPage } from '@/pages/mypage/MyPage';

/**
 * 바깥 주소로 나가는 라우트. {@code Navigate} 는 앱 안에서만 움직이므로 http 주소를 넘길 수 없다.
 *
 * <p>{@code replace} 로 나간다 — 뒤로 가기가 이 자리로 되돌아오면 곧바로 다시 바깥으로 튕겨
 * 나가서, 사용자는 <b>뒤로 갈 수 없는 화면</b>을 만난다.
 *
 * <p>{@code useEffect} 안에서 옮기는 것은 그리기 도중에 주소를 바꾸면 React 가 같은 이동을 두 번
 * 실행할 수 있기 때문이다. 나가는 동안 짧게 빈 칸 대신 스피너가 선다.
 */
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <LoadingBlock />;
}

/**
 * 설정에 적힌 바깥 주소로 나간다 (사용자 지시 2026-09-01 · {@link useExternalLinks}).
 *
 * <p><b>기다렸다 나간다.</b> 응답이 오기 전에 화면 상수로 먼저 내보내면, 주소를 바꿔 둔 회사에서
 * 링크로 들어온 사람만 <b>옛 주소</b>로 가게 된다 — 메뉴로 간 사람과 다른 곳에 도착하는 것이
 * 이 값을 한 곳에 모아 둔 이유 그 자체다. 기다리는 동안 스피너가 선다.
 *
 * <p>응답이 <b>실패</b>했을 때만 화면 상수로 물러선다. 설정 API 하나가 흔들렸다고 메뉴가 아무 데도
 * 가지 못하는 것보다는, 기본 주소로라도 가는 편이 낫다.
 */
/**
 * 옛 주소를 새 자리로 넘기되 <b>질의 인자를 그대로 들고</b> 간다.
 *
 * <p>{@code Navigate} 에 경로만 주면 `?personnelId=`·`?undated=1` 이 통째로 날아간다 — 사람을
 * 골라 보낸 링크가 「아무도 고르지 않은 명부」 에 도착하고, 할 일을 짚어 보낸 링크는 화면만 연다.
 * 넘기는 자리마다 다시 짜지 않도록 여기 하나로 둔다.
 */
function KeepQueryRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/**
 * 옛 경력관리 주소(`/hr/management`)를 합쳐진 화면의 <b>경력 탭</b>으로 넘긴다 (2026-09-23).
 *
 * <p>{@link KeepQueryRedirect} 와 달리 `tab=career` 를 <b>얹는다</b>. 이 주소로 오는 링크는
 * 전부 이름이 「경력관리」 라 — 직원찾기 명함의 「경력관리에서 보기」가 그렇다 — 도착지도 그
 * 말이 가리키는 곳이어야 한다. 이미 `tab` 을 적어 온 링크는 그 뜻을 존중해 건드리지 않는다.
 */
/**
 * 인사자료 메뉴의 입구(`/hr/records`) — <b>대시보드부터</b> 연다 (사용자 지시 2026-11-04: 「대시보드가 먼저 안 뜨고
 * 인사자료가 뜨는 건 뭐야」). 하위 메뉴 첫 줄이 대시보드다(menuConfig sortOrder 0 · 2026-11-02).
 *
 * <p>주소에 무언가 실려 온 링크(`?personnelId=` · `?undated=1`)는 사람·명부를 짚은 것이라 지금처럼 인사자료로
 * 보낸다. 경력관리만 받은 사람은 대시보드 문이 없어 화면이 인사자료로 돌려보낸다({@code HrRecordsPage}).
 */
function HrRecordsEntry() {
  const { search } = useLocation();
  return search ? <KeepQueryRedirect to="/hr/records/staff" /> : <Navigate to="/hr/records/dashboard" replace />;
}

function CareerTabRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (!params.has('tab')) params.set('tab', 'career');
  return <Navigate to={`/hr/records/staff?${params.toString()}`} replace />;
}

function ExternalLinkRedirect({ code, fallback }: { code: string; fallback: string }) {
  const { data, isPending } = useExternalLinks();
  if (isPending) return <LoadingBlock />;
  return <ExternalRedirect to={data?.links.find((link) => link.code === code)?.url ?? fallback} />;
}

/**
 * v1 화면 → v2 라우트 대응표. 이관 진행 상황이 여기 한 곳에 드러난다.
 *
 * 경로는 v1 과 같아야 한다. 사내 사용자가 즐겨찾기·메신저로 공유해 둔 URL 이 살아 있어야
 * 도메인 단위 교체(Strangler Fig)가 사용자에게 보이지 않는다.
 *
 * `element` 가 없으면 아직 이관하지 않은 화면이며 안내 페이지가 뜬다.
 * 화면을 옮길 때 하는 일은 `element` 를 채우는 것뿐이다.
 *
 * ## 업무 화면은 눌렀을 때 받는다
 *
 * 화면 코드를 전부 첫 응답에 실으면, 대시보드만 보는 사람도 인사·설정·기술집 코드를 함께 받는다.
 * `lazy()` 로 감싸면 Vite 가 화면마다 조각을 따로 만들고 그 경로에 들어갈 때만 받아온다.
 * 기다리는 동안에는 `router.tsx` 의 `Suspense` 가 스피너를 띄운다.
 *
 * 껍데기(홈·마이페이지·로그인)는 감싸지 않는다. 로그인 직후 반드시 필요하거나
 * 헤더가 이미 들고 있어서 조각을 나눠도 요청만 한 번 더 늘 뿐이다.
 *
 * named export 를 `lazy()` 가 원하는 모양으로 바꾸느라 `.then()` 이 붙는다.
 * 이것 때문에 default export 로 되돌리지는 않는다 — 이름이 있어야 검색과 자동 import 가 산다.
 */
const DashboardPage = lazy(() => import('@/pages/meetings/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const WeeklyWorkPage = lazy(() => import('@/pages/meetings/WeeklyWorkPage').then((m) => ({ default: m.WeeklyWorkPage })));
const MonthlyWorkPage = lazy(() => import('@/pages/meetings/MonthlyWorkPage').then((m) => ({ default: m.MonthlyWorkPage })));
const SiteStatusPage = lazy(() => import('@/pages/sites/status/SiteStatusPage').then((m) => ({ default: m.SiteStatusPage })));
const CompletedSitesPage = lazy(() => import('@/pages/sites/status/CompletedSitesPage').then((m) => ({ default: m.CompletedSitesPage })));
const BidBoardPage = lazy(() => import('@/pages/sites/status/BidBoardPage').then((m) => ({ default: m.BidBoardPage })));
const RfqBoardPage = lazy(() => import('@/pages/sites/pmis/rfq/RfqBoardPage').then((m) => ({ default: m.RfqBoardPage })));
const SitePmisPage = lazy(() => import('@/pages/sites/pmis/SitePmisPage').then((m) => ({ default: m.SitePmisPage })));
const DailyReportRedirect = lazy(() => import('@/pages/sites/pmis/daily/DailyReportRedirect').then((m) => ({ default: m.DailyReportRedirect })));
const SiteChatsPage = lazy(() => import('@/pages/sites/chats/SiteChatsPage').then((m) => ({ default: m.SiteChatsPage })));
const ContactsDirectoryPage = lazy(() => import('@/pages/sites/contacts/ContactsDirectoryPage').then((m) => ({ default: m.ContactsDirectoryPage })));
const OfficeDashboardPage = lazy(() => import('@/pages/office/OfficeDashboardPage').then((m) => ({ default: m.OfficeDashboardPage })));
const PersonnelSearchPage = lazy(() => import('@/pages/hr/PersonnelSearchPage').then((m) => ({ default: m.PersonnelSearchPage })));
const PlacementPage = lazy(() => import('@/pages/hr/PlacementPage').then((m) => ({ default: m.PlacementPage })));
// 경력관리 화면(`PersonnelManagementPage`)은 2026-09-23 에 없앴다 — 그 본론은 인사자료의
// 「경력」 탭(`CareerPanel`)이고, 옛 주소는 `CareerTabRedirect` 가 그 탭으로 넘긴다.
const TechnicianStatusPage = lazy(() => import('@/pages/hr/TechnicianStatusPage').then((m) => ({ default: m.TechnicianStatusPage })));
const HrRecordsPage = lazy(() => import('@/pages/hr/HrRecordsPage').then((m) => ({ default: m.HrRecordsPage })));
// 근태 관리(옛 출결 관리 · 2026-09-03) — 찍는 것은 앱, 보는 것은 웹뿐이다. 2026-10-26 연차관리 탭을 들였다.
const AttendancePage = lazy(() => import('@/pages/hr/AttendancePage').then((m) => ({ default: m.AttendancePage })));
const PcOffPage = lazy(() => import('@/pages/hr/pcoff/PcOffPage').then((m) => ({ default: m.PcOffPage })));
const LeaveManagementPage = lazy(() => import('@/pages/hr/LeaveManagementPage').then((m) => ({ default: m.LeaveManagementPage })));
const DocTemplatesPage = lazy(() => import('@/pages/hr/DocTemplatesPage').then((m) => ({ default: m.DocTemplatesPage })));
const HrStandardsPage = lazy(() => import('@/pages/hr/HrStandardsPage').then((m) => ({ default: m.HrStandardsPage })));
const DiningGuidePage = lazy(() => import('@/pages/news/DiningGuidePage').then((m) => ({ default: m.DiningGuidePage })));
const NoticeBoardPage = lazy(() => import('@/pages/news/NoticeBoardPage').then((m) => ({ default: m.NoticeBoardPage })));
const EmployeeNewsPage = lazy(() => import('@/pages/news/EmployeeNewsPage').then((m) => ({ default: m.EmployeeNewsPage })));
const ConstructionTrendsPage = lazy(() => import('@/pages/news/ConstructionTrendsPage').then((m) => ({ default: m.ConstructionTrendsPage })));
const NewsHeadlinesPage = lazy(() => import('@/pages/news/NewsHeadlinesPage').then((m) => ({ default: m.NewsHeadlinesPage })));
const InternalGuidelinesPage = lazy(() => import('@/pages/news/InternalGuidelinesPage').then((m) => ({ default: m.InternalGuidelinesPage })));
const RequestBoardPage = lazy(() => import('@/pages/requests/RequestBoardPage').then((m) => ({ default: m.RequestBoardPage })));
const ThanksBoardPage = lazy(() => import('@/pages/board/ThanksBoardPage').then((m) => ({ default: m.ThanksBoardPage })));
const ThanksPostPage = lazy(() => import('@/pages/board/ThanksPostPage').then((m) => ({ default: m.ThanksPostPage })));
const TechCollectionPage = lazy(() => import('@/pages/tech/TechCollectionPage').then((m) => ({ default: m.TechCollectionPage })));
const DocsHomePage = lazy(() => import('@/pages/docs/DocsHomePage').then((m) => ({ default: m.DocsHomePage })));
const DocsS3Page = lazy(() => import('@/pages/docs/DocsS3Page').then((m) => ({ default: m.DocsS3Page })));
const DocsNasPage = lazy(() => import('@/pages/docs/DocsNasPage').then((m) => ({ default: m.DocsNasPage })));
const DocUploadWizard = lazy(() => import('@/pages/docs/DocUploadWizard').then((m) => ({ default: m.DocUploadWizard })));
const DocCategoriesPage = lazy(() => import('@/pages/docs/DocCategoriesPage').then((m) => ({ default: m.DocCategoriesPage })));
const DocsViewerPage = lazy(() => import('@/pages/docs/viewer/DocsViewerPage').then((m) => ({ default: m.DocsViewerPage })));
const OfficeEditorPage = lazy(() => import('@/pages/docs/office/OfficeEditorPage').then((m) => ({ default: m.OfficeEditorPage })));
const LabPage = lazy(() => import('@/pages/docs/lab/LabPage').then((m) => ({ default: m.LabPage })));
const SpecSheetPage = lazy(() => import('@/pages/sheet/SpecSheetPage').then((m) => ({ default: m.SpecSheetPage })));
const LedgerWorkbookPage = lazy(() => import('@/pages/sheet/LedgerWorkbookPage').then((m) => ({ default: m.LedgerWorkbookPage })));
const StorageAuditPage = lazy(() =>
  import('@/pages/docs/StorageAuditPage').then((m) => ({ default: m.StorageAuditPage })),
);
const DocSubmissionsPage = lazy(() =>
  import('@/pages/docs/submissions/DocSubmissionsPage').then((m) => ({ default: m.DocSubmissionsPage })),
);
const DepartmentsPage = lazy(() => import('@/pages/settings/DepartmentsPage').then((m) => ({ default: m.DepartmentsPage })));
const BackupPage = lazy(() => import('@/pages/settings/BackupPage').then((m) => ({ default: m.BackupPage })));
const MailSettingsPage = lazy(() => import('@/pages/settings/MailSettingsPage').then((m) => ({ default: m.MailSettingsPage })));
const ExternalApiKeysPage = lazy(() => import('@/pages/settings/ExternalApiKeysPage').then((m) => ({ default: m.ExternalApiKeysPage })));
const PushSettingsPage = lazy(() => import('@/pages/settings/PushSettingsPage').then((m) => ({ default: m.PushSettingsPage })));
const ExitSurveyFormPage = lazy(() => import('@/pages/settings/ExitSurveyFormPage').then((m) => ({ default: m.ExitSurveyFormPage })));
const ShortcutLinksPage = lazy(() => import('@/pages/settings/ShortcutLinksPage').then((m) => ({ default: m.ShortcutLinksPage })));
const LoginDesignPage = lazy(() => import('@/pages/settings/LoginDesignPage').then((m) => ({ default: m.LoginDesignPage })));
const SafetyAdminsPage = lazy(() => import('@/pages/settings/SafetyAdminsPage').then((m) => ({ default: m.SafetyAdminsPage })));
const HomepageAdminPage = lazy(() => import('@/pages/settings/homepage/HomepageAdminPage').then((m) => ({ default: m.HomepageAdminPage })));
const PollBoardPage = lazy(() => import('@/pages/news/PollBoardPage').then((m) => ({ default: m.PollBoardPage })));
const SiteManagerPage = lazy(() => import('@/pages/settings/SiteManagerPage').then((m) => ({ default: m.SiteManagerPage })));
const MenuManagerPage = lazy(() => import('@/pages/settings/MenuManagerPage').then((m) => ({ default: m.MenuManagerPage })));
const UserManagerPage = lazy(() => import('@/pages/settings/UserManagerPage').then((m) => ({ default: m.UserManagerPage })));
const RoleManagerPage = lazy(() => import('@/pages/settings/RoleManagerPage').then((m) => ({ default: m.RoleManagerPage })));
const ErrorLogPage = lazy(() => import('@/pages/settings/ErrorLogPage').then((m) => ({ default: m.ErrorLogPage })));
const ReleaseNotesPage = lazy(() => import('@/pages/settings/ReleaseNotesPage').then((m) => ({ default: m.ReleaseNotesPage })));
const PermissionDiagnosisPage = lazy(() => import('@/pages/settings/PermissionDiagnosisPage').then((m) => ({ default: m.PermissionDiagnosisPage })));
const MyApprovalPage = lazy(() => import('@/pages/approval/MyApprovalPage').then((m) => ({ default: m.MyApprovalPage })));
const ApprovalDraftPage = lazy(() => import('@/pages/approval/ApprovalDraftPage').then((m) => ({ default: m.ApprovalDraftPage })));
const TechEducationRefundPage = lazy(() => import('@/pages/hr/TechEducationRefundPage').then((m) => ({ default: m.TechEducationRefundPage })));
const TechEducationEvidencePage = lazy(() => import('@/pages/hr/TechEducationEvidencePage').then((m) => ({ default: m.TechEducationEvidencePage })));
const ApprovalRulesPage = lazy(() => import('@/pages/approval/ApprovalRulesPage').then((m) => ({ default: m.ApprovalRulesPage })));
const ApprovalDelegationPage = lazy(() => import('@/pages/approval/ApprovalDelegationPage').then((m) => ({ default: m.ApprovalDelegationPage })));
const SupplyRequisitionPage = lazy(() => import('@/pages/assets/SupplyRequisitionPage').then((m) => ({ default: m.SupplyRequisitionPage })));
const MaterialLedgerPage = lazy(() => import('@/pages/assets/MaterialLedgerPage').then((m) => ({ default: m.MaterialLedgerPage })));
const BusinessCardsPage = lazy(() => import('@/pages/generalaffairs/BusinessCardsPage').then((m) => ({ default: m.BusinessCardsPage })));
const ExpenseStandardsPage = lazy(() => import('@/pages/generalaffairs/ExpenseStandardsPage').then((m) => ({ default: m.ExpenseStandardsPage })));
const ExpenseRequestsPage = lazy(() => import('@/pages/generalaffairs/ExpenseRequestsPage').then((m) => ({ default: m.ExpenseRequestsPage })));
const CertificatesPage = lazy(() => import('@/pages/generalaffairs/CertificatesPage').then((m) => ({ default: m.CertificatesPage })));
const ReservationsPage = lazy(() => import('@/pages/generalaffairs/ReservationsPage').then((m) => ({ default: m.ReservationsPage })));
const AssetItemsPage = lazy(() => import('@/pages/generalaffairs/AssetItemsPage').then((m) => ({ default: m.AssetItemsPage })));
const DispatchPage = lazy(() => import('@/pages/generalaffairs/DispatchPage').then((m) => ({ default: m.DispatchPage })));
const RemoteSupportPage = lazy(() => import('@/pages/remotesupport/RemoteSupportPage').then((m) => ({ default: m.RemoteSupportPage })));
const StaffAssetsPage = lazy(() => import('@/pages/assets/StaffAssetsPage').then((m) => ({ default: m.StaffAssetsPage })));
const MealsPage = lazy(() => import('@/pages/assets/MealsPage').then((m) => ({ default: m.MealsPage })));
const SiteInventoryPage = lazy(() => import('@/pages/generalaffairs/SiteInventoryPage').then((m) => ({ default: m.SiteInventoryPage })));
const SoftwareLicensesPage = lazy(() => import('@/pages/generalaffairs/SoftwareLicensesPage').then((m) => ({ default: m.SoftwareLicensesPage })));
const AssetMovementsPage = lazy(() => import('@/pages/generalaffairs/AssetMovementsPage').then((m) => ({ default: m.AssetMovementsPage })));
const MaterialPricesPage = lazy(() => import('@/pages/generalaffairs/MaterialPricesPage').then((m) => ({ default: m.MaterialPricesPage })));
const CourierPage = lazy(() => import('@/pages/generalaffairs/CourierPage').then((m) => ({ default: m.CourierPage })));
const VendorMasterPage = lazy(() => import('@/pages/vendors/VendorMasterPage').then((m) => ({ default: m.VendorMasterPage })));
const AiAgentPage = lazy(() => import('@/pages/aiagent/AiAgentPage').then((m) => ({ default: m.AiAgentPage })));
const TodoSchedulePage = lazy(() => import('@/pages/todos/TodoSchedulePage').then((m) => ({ default: m.TodoSchedulePage })));
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })));
export type Screen = {
  path: string;
  /**
   * 접근 가능한 메뉴 키. `RequireAuth` 가 화면을 돌려보내는 데 쓴다 — 인가가 아니라 UX 용이다.
   * 비워 두면 로그인만 확인한다.
   */
  menuKey?: string;
  label: string;
  /** 대응하는 v1 소스. 이관할 때 무엇을 보면 되는지 남긴다. */
  v1Source: string;
  element?: ReactElement;
};

export const SCREENS: Screen[] = [
  // 로그인 후 첫 화면. menuKey 를 걸지 않는다 — 권한이 없어 다른 화면에서 되돌아오는 곳이므로
  // 여기까지 막히면 갈 곳이 없어진다.
  { path: '/home', label: '홈', v1Source: '(v2 신규)', element: <HomePage /> },

  // 보미소식
  { path: '/news/notices', menuKey: 'news_notices', label: '공지사항', v1Source: 'components/NoticeBoardClient.tsx', element: <NoticeBoardPage /> },
  { path: '/news/polls', menuKey: 'news_polls', label: '투표', v1Source: '(v2 신규)', element: <PollBoardPage /> },
  { path: '/news/employees', menuKey: 'news_employee', label: '입/퇴사자 소식', v1Source: 'components/EmployeeNewsClient.tsx', element: <EmployeeNewsPage /> },
  { path: '/news/dining', menuKey: 'news_dining', label: '식당안내', v1Source: 'components/DiningGuideClient.tsx', element: <DiningGuidePage /> },
  { path: '/news/trends', menuKey: 'news_trends', label: '건설동향', v1Source: 'components/ConstructionTrendsClient.tsx', element: <ConstructionTrendsPage /> },
  { path: '/news/headlines', menuKey: 'news_headlines', label: '뉴스', v1Source: '(v2 신규)', element: <NewsHeadlinesPage /> },
  { path: '/news/guidelines', menuKey: 'news_guidelines', label: '사내지침&매뉴얼', v1Source: 'components/InternalGuidelinesClient.tsx', element: <InternalGuidelinesPage /> },

  /*
    보미기술집 — 한 화면 <b>두 탭</b>이다 (사용자 지시 2026-09-22 · {@link TechCollectionPage}).

    <p>옛 주소 둘은 <b>그대로 남는다</b>. 사내에서 공유해 둔 링크가 있고, 탭 줄도 이 주소를 가리킨다 —
    상태가 아니라 링크라야 뒤로 가기가 탭을 되돌린다.

    <p>`/tech/performance` 는 <b>없앴다</b>. 보미 실적은 현장 조회의 준공 탭 안으로 들어갔다
    ({@code PerformancePanel}) — 실적 한 장이 담는 것이 결국 그 현장의 준공 기록이라, 현장을 떠나
    따로 선 목록으로 둘 이유가 없어졌다. 준공 현장의 목록은 현장 현황판의 준공 덩이가 세운다.
  */
  { path: '/tech', menuKey: 'tech', label: '보미기술집', v1Source: '(v2 신규 · 탭 묶음)', element: <TechCollectionPage /> },
  { path: '/tech/cases', menuKey: 'tech_cases', label: '공종별 기술집', v1Source: 'components/TechnicalBoardClient.tsx (kind="case")', element: <TechCollectionPage tab="cases" /> },
  { path: '/tech/new', menuKey: 'tech_new', label: '신기술 소개', v1Source: 'components/TechnicalBoardClient.tsx (kind="new")', element: <TechCollectionPage tab="new" /> },

  // 현장 — 현장 정보(카드 보드, 요약) + 현장 조회(PMIS 상세, DOC-12).
  //
  // 두 화면의 권한 키가 다르다. 현장 정보는 요약이라 전 직원 공개에 가깝지만(`site_status`), 현장 조회는
  // 일보·기성·검측 같은 운영 기록이라 `site_pmis` 를 따로 요구한다 — DOC-17 "요약은 공개, 상세·편집은 격리".
  // 카드 클릭으로만 진입하므로 메뉴 골격에는 없고, 이 라우트 가드가 유일한 프론트 입구다.
  { path: '/sites/status', menuKey: 'site_status', label: '현장 현황', v1Source: 'components/SiteStatusBoardClient.tsx', element: <SiteStatusPage /> },
  // 현장 현황 › 진행(V303). 부모(`/sites/status`)와 <b>같은 화면</b>이고 주소만 다르다 —
  // 주간업무가 부모 `/meetings/weekly` 와 하위 `/meetings/hq` 를 나눠 둔 것과 같은 규칙이다
  // (한 주소를 두 메뉴가 가리키면 사이드바의 활성 표시가 둘 다 켜진다 · `menuRoutes.test`).
  { path: '/sites/status/active', menuKey: 'site_status', label: '진행', v1Source: 'components/SiteStatusBoardClient.tsx', element: <SiteStatusPage /> },
  // 현장 현황 › 입찰(V303). 진행 보드와 <b>키가 다르다</b> — 아직 수주하지 못한 금액이 실려,
  // 누가 보고 누가 넣는지를 따로 정한다. 메뉴는 게이트로 닫혀 있다(오픈 전에는 관리자만 본다).
  { path: '/sites/status/bids', menuKey: 'site_status_bid', label: '입찰', v1Source: '(v2 신규)', element: <BidBoardPage /> },
  // 입찰 › 입찰(2026-09-30). 부모와 <b>같은 화면</b>이고 주소만 다르다 — 진행(`/sites/status/active`)이
  // 부모와 주소를 나눈 것과 같은 규칙이다(한 주소를 두 메뉴가 가리키면 활성 표시가 둘 다 켜진다).
  { path: '/sites/status/bids/tender', menuKey: 'site_status_bid', label: '입찰', v1Source: '(v2 신규)', element: <BidBoardPage /> },
  /*
    현장 현황 › 입찰 › <b>견적</b> (사용자 지시 2026-09-30 · DOC-98). 협력사에게 견적·입찰을
    <b>물어서 받는</b> 쪽이다 — 위의 입찰(`/bids`)은 발주처 공고에 우리가 투찰하는 쪽이라
    방향이 반대다. 나이스다큐가 하는 일이 이쪽이다.

    ★ 현장 조회 › 견적·입찰 탭을 걷고 여기로 옮겼다. 키는 <b>그대로</b> `site_pmis_rfq` 다(V359) —
      화면이 옮겨 갔다고 키를 새로 만들면 V359 가 붙여 둔 역할 그랜트가 아무 문도 열지 못하고,
      게이트에 닫아 둔 줄도 짝을 잃는다. 라우트 가드와 사이드바가 그 한 키를 함께 본다.
  */
  { path: '/sites/status/rfqs', menuKey: 'site_pmis_rfq', label: '견적', v1Source: '(v2 신규 · DOC-98)', element: <RfqBoardPage /> },
  /*
    현장 현황 › 준공 (사용자 지시 2026-09-29). 끝난 현장에서 하는 일이 전부 여기 있다 —
    하자 접수함 · 그 현장 게시판 · 입주민 승인 · 시설·매뉴얼 · 설정.

    ★ 한때 최상위 「CS」 메뉴 넷과 게시판의 「하자 접수」 한 줄로 흩어져 있었다. 같은 날
      걷어 이 한 칸으로 모았다 — 입주민의 하자도 게시판도 <b>그 현장의 일</b>이다.
  */
  { path: '/sites/status/completed', menuKey: 'site_status_completed', label: '준공', v1Source: '(v2 신규 · DOC-95)', element: <CompletedSitesPage /> },
  { path: '/sites/pmis', menuKey: 'site_status', label: '현장 조회', v1Source: '(v2 신규)', element: <SitePmisPage /> },
  // 공사일보 한 장으로 가는 짧은 경로(DOC-117 §5) — 할일의 「공사일보 결재」 가 일보 id 만 들고 와서 그 현장·그 날짜로 넘긴다.
  { path: '/sites/pmis/daily-report/:id', menuKey: 'site_status', label: '공사일보', v1Source: '(v2 신규 · DOC-117)', element: <DailyReportRedirect /> },
  // 현장 대화방 목록(DOC-26). 읽기 전용이라 현장 정보와 같은 요약 키로 연다 — 등록·수정은 현장 조회 안에서 한다.
  { path: '/sites/chats', menuKey: 'site_status', label: '현장 대화방', v1Source: '(v2 신규)', element: <SiteChatsPage /> },
  /*
    연락처(2026-09-22) — 현장 현황 머리말의 「대화방」 옆 버튼으로 들어온다. 전에는 현장 전체
    대시보드의 둘째 탭이었다.

    ★ 키는 <b>`site_pmis`</b> 다. 대화방과 달리 요약이 아니라 전 현장 담당자의 이름·번호라,
      현장 조회와 같은 열쇠를 요구한다 — 서버도 같은 키로 막는다(`ContactDirectoryController`).
      대화방처럼 `site_status` 로 두면 프론트는 열리고 서버가 403 을 주어, 화면이 빈 표가 된다.
  */
  { path: '/sites/contacts', menuKey: 'site_pmis_contacts', label: '연락처', v1Source: '(v2 신규)', element: <ContactsDirectoryPage /> },
  // 공무 대시보드(DOC-37 §10) — 현장 정보 화면의 카드로 들어온다. 메뉴 골격에는 두지 않는다.
  // 권한 키는 서버 착수 때 `office_dashboard` 로 판다 — 지금 새 키를 쓰면 그랜트가 없어 아무에게도 안 보인다.
  // 키를 `site_status` 에서 <b>이 화면의 실제 키</b>로 바로잡았다(2026-08-28). 백엔드가 이미
  // `office_dashboard` 로 막고 있어(OfficeDashboardController) 권한 없는 사람은 여기까지 들어와
  // 빈 표를 보고 있었고, 오픈 게이트도 이 키로만 닫을 수 있다.
  // 이름은 화면 제목과 같은 「현장 전체 대시보드」다(2026-08-28) — 탭이 개요·연락처·매출·원가·수금·
  // 주요자재 넷이 되면서 「공무」는 그중 하나의 이름이 됐다.
  { path: '/office/dashboard', menuKey: 'office_dashboard', label: '현장 전체 대시보드', v1Source: '(v2 신규)', element: <OfficeDashboardPage /> },

  // 업무 일정 — 대시보드 · 주간업무 · 월간업무.
  //
  // 주간업무·월간업무는 각각 한 화면이고 그 안이 상단 탭이다. 탭마다 <b>경로를 따로 둔다</b> —
  // v1 부터 쓰던 주소(`/meetings/hq`·`/meetings/monthly/site` …)를 즐겨찾기·메신저로 공유해 둔 사람이 많고,
  // 리다이렉트로 뭉개면 그 사람들이 매번 다른 탭에서 시작한다. 경로가 곧 탭이면 링크 공유도 그대로 산다.
  { path: '/dashboard', menuKey: 'dashboard', label: '대시보드', v1Source: 'components/DashboardClient.tsx', element: <DashboardPage /> },

  { path: '/meetings/weekly', menuKey: 'work_weekly', label: '주간업무', v1Source: '(v2 신규 · 세 화면 통합)', element: <WeeklyWorkPage /> },
  { path: '/meetings/hq', menuKey: 'meetings_hq', label: '주간업무 · 본사', v1Source: 'components/HQMeetingClient.tsx', element: <WeeklyWorkPage tab="hq" /> },
  { path: '/meetings/site', menuKey: 'meetings_site', label: '주간업무 · 현장', v1Source: 'components/SiteMeetingClient.tsx', element: <WeeklyWorkPage tab="site" /> },
  { path: '/meetings/completed', menuKey: 'meetings_completed', label: '주간업무 · 준공현장', v1Source: 'components/CompletedSiteClient.tsx', element: <WeeklyWorkPage tab="completed" /> },

  { path: '/meetings/monthly', menuKey: 'work_monthly', label: '월간업무', v1Source: '(v2 신규 · 통합 달력 + 세 화면)', element: <MonthlyWorkPage /> },
  // '전체' 탭의 제 주소. 부모 주소와 같은 얼굴이지만 둘째 줄 탭이 가리킬 자기 경로가 있어야 한다(메뉴는 경로를 나눠 쓴다).
  { path: '/meetings/monthly/all', menuKey: 'calendar', label: '월간업무 · 전체', v1Source: '(v2 신규 · 통합 달력)', element: <MonthlyWorkPage tab="all" /> },
  { path: '/meetings/monthly/hq', menuKey: 'monthly_hq', label: '월간업무 · 본사', v1Source: 'components/MonthlyMeetingClient.tsx (scope="hq")', element: <MonthlyWorkPage tab="hq" /> },
  { path: '/meetings/monthly/field', menuKey: 'monthly_field', label: '월간업무 · 현장 주요일정', v1Source: 'components/MonthlyMeetingClient.tsx (scope="field")', element: <MonthlyWorkPage tab="field" /> },
  { path: '/meetings/monthly/site', menuKey: 'monthly_site', label: '월간업무 · 현장 회의일정', v1Source: 'components/MonthlyMeetingClient.tsx (scope="site")', element: <MonthlyWorkPage tab="site" /> },

  // 인사회의
  { path: '/hr/placement', menuKey: 'hr_placement', label: '인력배치표', v1Source: 'components/PlacementClient.tsx', element: <PlacementPage /> },
  { path: '/hr/attendance', menuKey: 'hr_attendance', label: '근태 관리', v1Source: '(v2 신규)', element: <AttendancePage /> },
  { path: '/hr/pc-off', menuKey: 'hr_pc_off', label: 'PC Off', v1Source: '(v2 신규 · DOC-77)', element: <PcOffPage /> },
  // 사이드바 줄은 2026-09-23 에 내려갔다(`RETIRED_MENU_KEYS`) — 묻는 자리가 AI Agent 대화로 옮겨 갔다.
  // ★ 라우트는 남긴다: 모바일 앱이 이 주소를 계속 쓰고, 에이전트의 화면 목록도 이 경로를 대조한다
  //   (`agent/tests/test_screens_sync.py`). 이름만 메뉴와 맞춰 바로잡는다.
  { path: '/hr/search', menuKey: 'hr_search', label: 'AI 경력매치', v1Source: 'components/PersonnelSearch.tsx', element: <PersonnelSearchPage /> },
  /*
    ★ **경력관리는 화면이 아니라 탭이 되었다** (사용자 지시 2026-09-23 · {@link HrRecordsPage}).

    <p>주소는 <b>지우지 않는다.</b> 직원찾기 명함의 「경력관리에서 보기」({@code PersonCard}),
    기술인 관리의 이름 링크, 그리고 사내에서 즐겨찾기·메신저로 공유해 둔 주소가 이곳을 가리킨다 —
    모바일 앱도 이 주소를 쓴다. 옛 주소를 끊는 것은 이관의 전제를 스스로 깨는 일이다.

    ★ `?personnelId=` 를 <b>그대로 싣고</b> 넘긴다. 빈손으로 넘기면 사람을 골라 보낸 링크가
      「아무도 고르지 않은 명부」 에 도착해, 보낸 쪽이 한 일이 통째로 버려진다.
    ★ `?tab=career` 를 <b>얹는다</b>. 이 주소로 오는 링크의 이름은 전부 「경력관리」 이고,
      그 말이 가리키는 것은 이제 합쳐진 화면의 첫 탭이 아니라 <b>경력 탭</b>이다.

    ★ `menuKey` 는 그대로 {@code hr_management} 다 — 이 주소를 여는 열쇠가 바뀐 적은 없다.
  */
  { path: '/hr/management', menuKey: 'hr_management', label: '인력관리', v1Source: 'components/HRClient.tsx', element: <CareerTabRedirect /> },
  // 사이드바에 줄을 만들지 않는다 — 인사자료의 「기술인 관리」 로 건너간다(DOC-54 §11).
  { path: '/hr/technicians', menuKey: 'hr_management', label: '기술인 관리', v1Source: '(v2 신규 · 협회 관리현황 시트)', element: <TechnicianStatusPage /> },
  /*
    ★ **부모 주소는 첫 화면으로 보낸다** (사용자 지적 2026-08-28: 「인사자료 관리를 누르면
      바로 직원자료가 켜지는데, 메뉴에서는 직원관리가 눌린거처럼 안보여」).

    같은 화면을 두 주소가 가리키면 사이드바는 <b>누른 줄</b>만 켜다 — 부모로 들어오면
    화면은 직원 자료인데 「직원 자료」 줄은 꺼져 있다. 그래서 부모는 그리지 않고 <b>넘긴다</b>.
    즐겨찾기는 그대로 살아 있고(넘어간다), 상위 줄은 자식이 켜지면 함께 켜진다(`useMenuTree.isActive`).
  */
  { path: '/hr/records', menuKey: 'hr_records_staff', label: '인사자료', v1Source: '(v2 신규)', element: <HrRecordsEntry /> },
  /*
    인사자료의 화면들이 각각 주소를 갖는다(2026-08-28 · 메뉴 하위로 내려갔다).
    ★ 다섯의 `menuKey` 는 전부 `hr_records` 다 — 라우트 가드가 보는 키를 나누면 이미 부여해 둔
      권한이 무효가 되고, 서버의 문(`MenuGuard`)도 그 키 하나다.
    ★ `/hr/records` 를 지우지 않는다 — 즐겨찾기·공유해 둔 주소가 그대로 열려야 한다.

    ★ **직원 자료(와 그리로 넘기는 부모)만 키가 다르다** (2026-09-23 · 경력관리를 합치면서).
      그 화면은 경력 탭을 품어 {@code hr_management} 로도 열려야 하는데, 라우트 가드는 키
      <b>하나</b>만 보므로 「둘 중 하나」를 말할 자리가 골격의 {@code visibleWhenAnyOf} 뿐이다
      ({@code MENU_KEY_ALIASES} → `hr_records_staff` = 자기 + `hr_records` + `hr_management`).
      나머지 여섯은 여기서 좁히지 않고 <b>화면이 되돌려 보낸다</b> — 그 이유는 {@code HrRecordsPage}
      의 `canRecords` 주석에 적어 두었다.
  */
  { path: '/hr/records/staff', menuKey: 'hr_records_staff', label: '인사자료', v1Source: '(v2 신규 · 경력관리 통합)', element: <HrRecordsPage view="records" /> },
  // 연차관리는 근태 관리의 탭이 됐다(사용자 지시 2026-10-26). 옛 주소는 걸어 둔 링크·즐겨찾기를 위해 남겨 그 탭으로 넘긴다.
  { path: '/hr/records/dashboard', menuKey: 'hr_records', label: '대시보드', v1Source: '(v2 신규)', element: <HrRecordsPage view="dashboard" /> },
  { path: '/hr/records/promotion', menuKey: 'hr_attendance', label: '연차관리', v1Source: '(v2 신규)', element: <Navigate to="/hr/attendance?tab=leave" replace /> },
  // 연도별 고과(V181 · 요청 No.21-2). 한 해 · 전 직원을 한 장으로 매긴다 — 이 값이 승진 요건의 고과 축이다.
  { path: '/hr/records/evaluation', menuKey: 'hr_records', label: '인사고과', v1Source: '(v2 신규)', element: <HrRecordsPage view="evaluation" /> },
  // 승진 관리 — 사이드바에 줄이 없다(menuConfig.ts). 직원 자료 화면의 [승진 관리] 버튼으로 들어온다.
  { path: '/hr/records/duties', menuKey: 'hr_records', label: '업무분장', v1Source: '(v2 신규)', element: <HrRecordsPage view="duties" /> },
  { path: '/hr/records/promotions', menuKey: 'hr_records', label: '승진 관리', v1Source: '(v2 신규)', element: <HrRecordsPage view="promotions" /> },
  { path: '/hr/records/holiday', menuKey: 'hr_records', label: '공휴일', v1Source: '(v2 신규)', element: <Navigate to="/hr/attendance?tab=publicHoliday" replace /> },
  { path: '/hr/records/standards', menuKey: 'hr_records', label: '복지·수당 기준', v1Source: '(v2 신규)', element: <HrRecordsPage view="standards" /> },
  { path: '/hr/records/relocation', menuKey: 'hr_records', label: '숙소·귀향', v1Source: '(v2 신규)', element: <HrRecordsPage view="relocation" /> },
  { path: '/hr/records/incentive', menuKey: 'hr_records', label: '성과급', v1Source: '(v2 신규)', element: <HrRecordsPage view="incentive" /> },
  // 급여명세서(V518). 문은 급여 담당 키 하나다 — 인사자료 키로 열면 급여 담당이 아닌 인사팀에게도 열린다.
  { path: '/hr/records/payroll', menuKey: 'hr_records_payroll', label: '급여명세서', v1Source: '(v2 신규)', element: <HrRecordsPage view="payroll" /> },
  /*
    ★ **지금 「휴무 관리」 는 계획서 한 줄이다** (사용자 지시 2026-08-31 · V174).

    대시보드·현황은 오픈 게이트에 닫힌 채로 두고(V162) 계획서만 열었다. 그래서 부모 줄을 눌러도
    닫힌 화면(대시보드)이 아니라 <b>열려 있는 것</b>으로 보낸다 — 「메뉴는 보이는데 눌렀더니
    막혔다」 가 되면 사용자는 자기 권한을 의심한다.

    ★ 세 화면의 `menuKey` 를 <b>각자의 키</b>로 바꿨다. 예전에는 셋 다 `hr_leave_status` 라
      부모가 열리는 순간 주소를 아는 사람이 대시보드·현황까지 그대로 열었다 — 사이드바에서만
      감춘 화면은 감춘 것이 아니다. 각자 키를 달면 게이트가 라우트에서도 판정한다.
      오픈하는 날에는 그 키의 게이트만 끄면 되고, 권한은 그대로 부모 키가 연다
      (`visibleWhenAnyOf` → `MENU_KEY_ALIASES`).

    ★ **사이드바는 더 이상 이 경로로 오지 않는다** (사용자 지시 2026-09-01). 부모 줄이 그룹이 되어
      누르면 펼쳐지기만 한다(`menuConfig`). 이 줄이 남아 있는 것은 <b>즐겨찾기·메신저로 돌던
      /hr/leave</b> 를 위해서다 — 그 주소로 들어온 사람에게 404 를 보이는 것보다 열려 있는 곳으로
      보내는 편이 낫다.
  */
  { path: '/hr/leave', menuKey: 'hr_leave_status', label: '휴무 관리', v1Source: '(v2 신규)', element: <Navigate to="/hr/leave/plan" replace /> },
  // { path: '/hr/leave', menuKey: 'hr_leave_status', label: '휴무 관리', v1Source: '(v2 신규)', element: <Navigate to="/hr/leave/dashboard" replace /> },
  { path: '/hr/leave/dashboard', menuKey: 'hr_leave_dashboard', label: '휴무 대시보드', v1Source: '(v2 신규)', element: <LeaveManagementPage tab="dashboard" /> },
  /*
    ★ **계획서는 잠시 바깥 주소로 보낸다** (사용자 지시 2026-08-31 · {@link LEAVE_PLAN_EXTERNAL_URL}).

    사이드바 항목만 바꾸면 <b>주소를 아는 사람</b>이 남는다 — 즐겨찾기·메신저로 돌던 링크와
    AI 가 안내하는 링크(`agent`)가 여전히 옛 화면을 연다. 그래서 경로도 함께 보낸다.

    ★ 경로를 지우지 않는 이유는 늘 같다 — /hr/leave/plan 이 404 가 되면 사용자에게는 기능이
      옮겨 간 것이 아니라 <b>시스템이 고장 난 것</b>으로 보인다. 되돌릴 때는 아래 줄을 살린다.

    ★ **주소는 설정에서 온다** (사용자 지시 2026-09-01 · 「환경설정 › 외부 API 설정」).
      상수는 <b>설정이 비었을 때의 기본값</b>이고, 사이드바(`useMenuTree`)도 같은 값을 본다 —
      두 곳이 한 값을 보아야 메뉴로 간 사람과 주소로 들어온 사람이 같은 곳에 도착한다.
  */
  { path: '/hr/leave/plan', menuKey: 'hr_leave_plan', label: '휴무 계획서', v1Source: '(v2 신규)', element: <ExternalLinkRedirect code="leave_plan" fallback={LEAVE_PLAN_EXTERNAL_URL} /> },
  // { path: '/hr/leave/plan', menuKey: 'hr_leave_status', label: '휴무 계획서', v1Source: '(v2 신규)', element: <LeaveManagementPage tab="plan" /> },
  { path: '/hr/leave/board', menuKey: 'hr_leave_board', label: '휴무 현황', v1Source: '(v2 신규)', element: <LeaveManagementPage tab="board" /> },
  /*
    ★ 모바일에서는 열지 않는다 (사용자 지시 2026-08-31 · {@link DesktopOnly}).
    칸을 끌어 놓고 미리보기와 견주며 좌표를 맞추는 화면이라, 좁은 화면에서 «되긴 되는» 상태로
    두면 어긋난 서식이 저장되고 그 서식으로 나가는 증명서가 전부 함께 어긋난다.
  */
  {
    path: '/hr/doc-templates',
    menuKey: 'hr_doc_templates',
    label: '증명서 서식 관리',
    v1Source: '(v2 신규)',
    element: (
      <DesktopOnly title="증명서 서식 관리" /* description="재직·경력 증명서에 값이 찍힐 자리를 서식 위에 배치합니다." */>
        <DocTemplatesPage />
      </DesktopOnly>
    ),
  },
  // 사이드바에 줄을 만들지 않는다 — 진급 패널·조직도가 「기준 미등록」 을 알릴 때 찾아오는 자리다(DOC-50).
  { path: '/hr/standards', menuKey: 'hr_records', label: '인사 기준표', v1Source: '(v2 신규)', element: <HrStandardsPage /> },

  // 자료실 — 문서 분류(대·중·소 관리) + 자료 조회(안내에서 S3·NAS 로 전환).
  { path: '/docs/categories', menuKey: 'docs_categories', label: '문서 분류', v1Source: '(v2 신규)', element: <DocCategoriesPage /> },
  { path: '/docs', menuKey: 'docs_browse', label: '자료 조회', v1Source: '(v2 신규)', element: <DocsHomePage /> },
  { path: '/docs/upload', menuKey: 'docs_browse', label: '문서 입력', v1Source: '(v2 신규)', element: <DocUploadWizard /> },
  { path: '/docs/s3', menuKey: 'docs_browse', label: '자료 조회 · S3', v1Source: '(v2 신규)', element: <DocsS3Page /> },
  { path: '/docs/nas', menuKey: 'docs_browse', label: '자료 조회 · NAS', v1Source: '(v2 신규)', element: <DocsNasPage /> },
  { path: '/docs/lab', menuKey: 'docs_lab', label: '실험실', v1Source: '(v2 신규)', element: <LabPage /> },
  // 감사 기록은 **사람의 행적**이라 자료실 권한이 아니라 보안 메뉴로 막는다(DOC-56 §1.4).
  {
    path: '/docs/audit',
    menuKey: 'settings_security',
    label: '자료실 감사 기록',
    v1Source: '(v2 신규)',
    element: <StorageAuditPage />,
  },
  // 요청·제출·검토 — 검토 전 자료는 staging/ 에 있어 자료 조회·AI 검색에 나타나지 않는다(DOC-48).
  {
    path: '/docs/submissions',
    menuKey: 'docs_submissions',
    label: '자료 요청',
    v1Source: '(v2 신규)',
    element: <DocSubmissionsPage />,
  },
  // 자료 뷰어 · 문서 편집기 — 둘 다 목록의 버튼으로만 들어오므로 메뉴 골격에는 없다.
  // 권한 키는 자료 조회와 같은 것을 쓴다 — 여는 것이 자료실 자료 그 자체이고,
  // 키를 새로 파면 그랜트가 없어 아무에게도 안 보인다.
  //
  // 둘 다 껍데기를 걷는다(`AppLayout` 의 CHROMELESS_PATHS) — 도면도 표도 클수록 읽힌다.
  /*
    자료 뷰어에는 <b>메뉴 키를 걸지 않는다</b>(2026-08-28). 계약서를 전 직원에게 열기로 하면서
    자료실 권한 없는 사람이 이 화면에 서게 됐다 — 키를 걸어 두면 목록에는 이름이 보이는데
    누르면 되돌려보내진다.

    <b>인가가 사라진 것이 아니라 옮겨 갔다.</b> 이 화면은 껍데기이고 바이트는 서버가 준다
    (`/v1/documents/browse/file`). 그 문이 자료실 권한 또는 현장 서류 자리로 판정한다
    (`DocumentBrowseController.opensBySiteDocSlot`) — 권한 없는 키를 넣으면 화면만 열리고 내용이 없다.
  */
  { path: '/docs/view', label: '자료 뷰어', v1Source: '(v2 신규 · 도면 뷰어 흡수)', element: <DocsViewerPage /> },
  { path: '/docs/office', menuKey: 'docs_browse', label: '문서 편집기', v1Source: '(v2 신규)', element: <OfficeEditorPage /> },

  /*
    입력 시트 — 대장마다 <b>우리 양식으로 된 엑셀</b>이 새 창으로 뜬다(2026-09-22).

    메뉴 골격에는 없다: 들어오는 길은 그 대장 화면의 [엑셀로 넣기] 뿐이다. 메뉴 키도 여기 걸지
    않는다 — 대장마다 키가 다르고, 그 판정은 주소의 낱말을 보고 화면이 직접 한다
    (`SHEET_SCREENS[specKey].menuKey`). 한 키를 대표로 걸면 그 키 없는 사람은 제 대장의 시트도
    못 연다.
  */
  { path: '/sheet/:specKey', label: '입력 시트', v1Source: '(v2 신규)', element: <SpecSheetPage /> },

  /*
    대장 엑셀로 가는 중간 역. <b>링크로만</b> 들어온다({@code ledgerWorkbookHref}) — 버튼에서
    window.open 을 부르면 서버 왕복 뒤라 팝업 차단에 걸린다(2026-09-22 실측).
  */
  { path: '/ledger/:ledgerKey', label: '대장 엑셀', v1Source: '(v2 신규)', element: <LedgerWorkbookPage /> },

  // 도면 뷰어(/bim)는 2026-08-18 에 자료실로 흡수됐다 — 아래 SCREEN_REDIRECTS 참고.

  // 요청게시판 > 개선요청
  { path: '/requests', menuKey: 'requests', label: '개선요청', v1Source: 'components/RequestBoardClient.tsx', element: <RequestBoardPage /> },

  // 게시판 > 윤리경영제보센터 (익명 · 제보센터 담당만 열람·답변)
  { path: '/board/thanks', menuKey: 'board_thanks', label: '윤리경영제보센터', v1Source: '(v2 신규)', element: <ThanksBoardPage /> },
  // 제보 하나. 메뉴에는 없고 목록의 줄에서 들어간다 — 주소가 있어야 뒤로 갈 수 있다.
  { path: '/board/thanks/:postId', menuKey: 'board_thanks', label: '제보 내용', v1Source: '(v2 신규)', element: <ThanksPostPage /> },

  // 전자결재
  /*
    전자결재는 <b>화면 하나</b>다 (사용자 지시 2026-09-22 — 사이드바 줄을 걷고 헤더 버튼으로 통일).
    옛 주소(`/approval`)도 같은 화면이 받는다: 알림 메일·할일 위젯·기술인 교육 화면이 `?box=`·`?doc=`
    를 달아 보내는 링크가 이미 나가 있고, 그 링크들이 가리키는 함은 지금도 그대로 있다.
  */
  { path: '/approval', menuKey: 'approval', label: '전자결재', v1Source: '(v2 신규)', element: <MyApprovalPage /> },
  { path: '/approval/my', menuKey: 'approval', label: '전자결재', v1Source: '(v2 신규)', element: <MyApprovalPage /> },
  { path: '/approval/draft', menuKey: 'approval', label: '기안하기', v1Source: '(v2 신규)', element: <ApprovalDraftPage /> },
  { path: '/approval/rules', menuKey: 'approval', label: '결재규정', v1Source: '(v2 신규)', element: <ApprovalRulesPage /> },
  { path: '/approval/delegation', menuKey: 'approval', label: '대결/위임', v1Source: '(v2 신규)', element: <ApprovalDelegationPage /> },

  // 자산관리
  // 자산 공지는 별도 화면이 아니라 이 화면 맨 위의 칸이다(AssetNoticePanel).
  { path: '/assets/supplies', menuKey: 'asset_supplies', label: '소모품·비품 신청', v1Source: '(v2 신규)', element: <SupplyRequisitionPage /> },
  // 같은 화면을 종류·역할만 바꿔 두 메뉴가 나눠 쓴다(2026-08-19 메뉴 정리).
  { path: '/assets/supplies/items', menuKey: 'asset_supplies_admin', label: '소모품 관리', v1Source: '(v2 신규)', element: <SupplyRequisitionPage view="items" /> },
  { path: '/assets/reservations/vehicles', menuKey: 'asset_vehicle_reservations', label: '차량 예약', v1Source: '(v2 신규)', element: <ReservationsPage fixedType="VEHICLE" /> },
  { path: '/assets/reservations/rooms', menuKey: 'asset_room_reservations', label: '회의실 예약', v1Source: '(v2 신규)', element: <ReservationsPage fixedType="ROOM" /> },
  { path: '/assets/certificates', menuKey: 'asset_certificates', label: '공인인증서', v1Source: '(v2 신규)', element: <CertificatesPage /> },
  { path: '/assets/items', menuKey: 'asset_items', label: '비품 자산 현황', v1Source: '(v2 신규)', element: <AssetItemsPage /> },
  { path: '/assets/site-inventory', menuKey: 'asset_site_inventory', label: '현장 자산 현황', v1Source: '(v2 신규)', element: <SiteInventoryPage /> },
  { path: '/assets/software', menuKey: 'asset_software', label: '소프트웨어 자산', v1Source: '(v2 신규)', element: <SoftwareLicensesPage /> },
  { path: '/assets/movements', menuKey: 'asset_movements', label: '자산 이동', v1Source: '(v2 신규)', element: <AssetMovementsPage /> },
  // 전 직원에게 나간 개인 자산(PC·모니터·휴대폰·태블릿)을 한 표로 본다. 등록은 인사기록 탭이
  // 하고 여기는 조회만 한다 — 한 자료에 손이 둘이면 어느 쪽이 맞는지 알 수 없게 된다.
  { path: '/assets/staff', menuKey: 'asset_staff', label: '직원 자산 현황', v1Source: '(v2 신규)', element: <StaffAssetsPage /> },
  // 총무 관리(DOC-120). 비품·직원 자산 현황은 주소를 그대로 두고 사이드바 자리만 옮겼다. 배차 관리는 차량 예약의
  // 탭이었다가 독립 화면이 됐다 — 할일 링크(TodoItemResponse)가 이 주소를 연다.
  { path: '/general-affairs/dispatch', menuKey: 'asset_dispatch', label: '배차 관리', v1Source: '(v2 신규)', element: <DispatchPage /> },
  // 원격지원(DOC-137 · V573). BoMI Lounge 가 깐 RustDesk 로 현장 PC 에 들어간다.
  { path: '/remote-support', menuKey: 'remote_support_pcs', label: 'PC 원격 접속', v1Source: '(v2 신규)', element: <RemoteSupportPage /> },
  // 안전 관리(DOC-122)는 안전 홈페이지(`safe/` 앱)로 떠났다(사용자 지시 2026-10-27). 옛 주소는 라우터가 넘긴다.
  // 식대 관리(V294). 식당 QR 을 찍은 줄이 여기 모인다 — 찍는 화면(`/meals/check`)은 사이드바
  // 바깥에 따로 서고(라우터), 이 메뉴는 그 결과를 세는 총무의 자리다.
  { path: '/assets/meals', menuKey: 'asset_meals', label: '식대 관리', v1Source: '(v2 신규)', element: <MealsPage /> },
  // 결재가 만든 행을 외주구매팀이 진행시키는 대장(요청 No.11 · 명함 취합과 같은 부류).
  // 「현장 신청 → 구매팀 확인 → 향후 기성첨부」 중 뒤의 두 걸음이 여기다.
  { path: '/assets/materials', menuKey: 'asset_material_requests', label: '잡자재 대장', v1Source: '(v2 신규)', element: <MaterialLedgerPage /> },
  // 홈 「환율 · 자재 단가」 위젯의 아래 칸이 읽는 표(V143). 환율은 수출입은행이 무료로 고시하지만
  // 철근 시중 유통단가는 무료로 받을 곳이 없어, 그 값만 여기서 손으로 넣는다.
  { path: '/assets/material-prices', menuKey: 'asset_material_prices', label: '자재 단가 설정', v1Source: '(v2 신규)', element: <MaterialPricesPage /> },

  // 총무 취합 대장(DOC-43 §14 2단계). 결재가 만든 행을 담당팀이 진행시키는 자리다.
  { path: '/hr/business-cards', menuKey: 'hr_business_cards', label: '명함 취합', v1Source: '(v2 신규)', element: <BusinessCardsPage /> },
  // 기술인 교육비 환급 큐(DOC-61 §3). 정산은 전자결재가 하고 여기서는 「지급했는가」 만 적는다.
  { path: '/hr/tech-education', menuKey: 'hr_tech_education', label: '기술인 교육', v1Source: '(v2 신규)', element: <TechEducationRefundPage /> },
  /*
    교육 증빙 제출(V264 · DOC-61 제2부). **메뉴 키를 걸지 않는다** — 홈 할일에서 눌러 들어오는
    자리이고, 그 할일은 교육을 신청한 모든 직원에게 뜬다. `hr_tech_education` 을 요구하면 정작
    증빙을 내야 하는 사람이 못 들어온다(그 키는 원장을 관리하는 사람의 문이다 · V117 §3).
    인가는 서버가 「그 교육의 주인인가」로 판정한다 — 마이페이지와 같은 결이다.
  */
  { path: '/hr/tech-education/evidence', label: '교육 증빙 제출', v1Source: '(v2 신규)', element: <TechEducationEvidencePage /> },
  { path: '/expenses/standards', menuKey: 'expense_standards', label: '경비 기준안', v1Source: '(v2 신규)', element: <ExpenseStandardsPage /> },
  { path: '/expenses/requests', menuKey: 'expense_requests', label: '경비 신청 대장', v1Source: '(v2 신규)', element: <ExpenseRequestsPage /> },
  { path: '/expenses/courier', menuKey: 'expense_courier', label: '퀵서비스 대장', v1Source: '(v2 신규)', element: <CourierPage /> },

  // 환경설정
  { path: '/settings/departments', menuKey: 'settings_departments', label: '부서 관리', v1Source: 'components/DepartmentManager.tsx', element: <DepartmentsPage /> },
  { path: '/settings/sites', menuKey: 'settings_sites', label: '현장 관리', v1Source: 'components/SiteManager.tsx', element: <SiteManagerPage /> },
  { path: '/settings/menus', menuKey: 'settings_menus', label: '메뉴 관리', v1Source: 'components/MenuManager.tsx', element: <MenuManagerPage /> },
  { path: '/settings/users', menuKey: 'settings_users', label: '사용자·권한 관리', v1Source: 'components/AdminTestClient.tsx', element: <UserManagerPage /> },
  { path: '/settings/roles', menuKey: 'settings_roles', label: '역할·권한 관리', v1Source: '(v2 신규)', element: <RoleManagerPage /> },
  /*
    권한 진단(2026-09-16) — 「이 사람은 무엇이 보이고, 왜 그런가」. <b>보는 자리</b>라 조회 하나뿐이다
    (`MenuActions` 의 READ). 고치는 문은 그대로 다섯 군데다.

    키를 역할·권한 관리와 나눈 이유는 권한 나무가 키 하나에 줄 하나를 요구해서다(`permissionTree`).
    V283 이 그쪽을 이미 보는 역할에게 이 키를 함께 켜 준다.
  */
  { path: '/settings/permission-diagnosis', menuKey: 'settings_permission_diagnosis', label: '권한 진단', v1Source: '(v2 신규)', element: <PermissionDiagnosisPage /> },
  { path: '/settings/backup', menuKey: 'settings_backup', label: '데이터 백업', v1Source: 'components/SettingsClient.tsx (backup)', element: <BackupPage /> },
  { path: '/settings/mail', menuKey: 'settings_mail', label: '메일 설정', v1Source: '(v2 신규)', element: <MailSettingsPage /> },
  // 앱 푸시(V222 · V244). 메일 설정과 갈라 둔 이유: 저쪽은 문구와 수신 주소를 고치는 자리고,
  // 이쪽 버튼은 누르는 즉시 남의 폰 잠금화면에 알림을 띄운다 — 같은 권한에 묶일 성격이 아니다.
  { path: '/settings/push', menuKey: 'settings_push', label: '푸시 알림', v1Source: '(v2 신규)', element: <PushSettingsPage /> },
  // 외부 API 키(V144). 조회부터 전산 담당 권한이 필요하다 — 「어떤 키가 설정돼 있는가」 자체가
  // 공격면을 알려 주는 정보라, 소프트웨어 자산이 라이선스 키 때문에 조회부터 막는 것과 같다.
  { path: '/settings/external-api', menuKey: 'settings_external_api', label: '외부 API 설정', v1Source: '(v2 신규)', element: <ExternalApiKeysPage /> },
  // 퇴사자 설문 양식(V146). 증명서 서식 관리(/hr/doc-templates)와 갈라 둔 이유: 저쪽은 직원이 요청해
  // 받아 가는 공문서와 회사 도장이고, 이쪽은 시스템이 자동으로 보내는 메일과 그 설문지다 — 메일 설정과
  // 같은 성격이라 환경설정에 선다.
  /* 서식 관리와 같은 이유로 모바일에서는 열지 않는다({@link DesktopOnly}). */
  {
    path: '/settings/exit-survey',
    menuKey: 'settings_exit_survey',
    label: '퇴사자 설문 양식',
    v1Source: '(v2 신규)',
    element: (
      <DesktopOnly title="퇴사자 설문 양식" /* description="퇴사자에게 보낼 설문의 문항과 순서를 짭니다." */>
        <ExitSurveyFormPage />
      </DesktopOnly>
    ),
  },
  // 오류 기록(V406). 최고 관리자 전용 — 라우트 가드는 SUPER_ADMIN_ONLY_MENU_KEYS, API 는 SUPER_ADMIN.
  { path: '/settings/error-log', menuKey: 'settings_error_log', label: '오류 기록', v1Source: '(v2 신규)', element: <ErrorLogPage /> },
  { path: '/settings/release-notes', menuKey: 'settings_release_notes', label: '변경이력', v1Source: '(v2 신규)', element: <ReleaseNotesPage /> },
  { path: '/settings/shortcuts', menuKey: 'settings_shortcuts', label: '바로가기 관리', v1Source: '(v2 신규)', element: <ShortcutLinksPage /> },
  /*
    로그인 화면(사용자 지시 2026-09-15). 시안 셋을 넣고 빼고, 사진과 글을 등록하고, 그 자리에서
    미리 본다. 모바일에서 열지 않을 이유는 없다 — 여기서 그리는 미리보기는 카드 한 칸이고,
    무거운 쪽(로그인 화면 본체)은 좁은 화면에서 스스로 서지 않는다(`LoginPage`).
  */
  { path: '/settings/login-design', menuKey: 'settings_login', label: '로그인 화면', v1Source: '(v2 신규)', element: <LoginDesignPage /> },
  { path: '/settings/homepage', menuKey: 'settings_homepage', label: '홈페이지 관리', v1Source: '(v2 신규)', element: <HomepageAdminPage /> },
  { path: '/settings/safety-admins', menuKey: 'settings_safety_admins', label: '안전관리 관리자', v1Source: '(v2 신규)', element: <SafetyAdminsPage /> },
  /*
    협력사·감리 마스터(V245~V247 · DOC-82 §2). <b>별도 메뉴·별도 페이지</b>다 — 하도급 탭 안에 넣지
    않는다(DOC-81 §5 결정 ③). 마스터는 현장의 것이 아니라 회사의 것이고, 현장 하나를 열어야 업체
    목록이 보이는 구조면 「우리 회사 협력사가 몇 곳인가」를 물을 자리가 없다.

    ★ 메뉴는 오픈 게이트에 <b>닫힌 채</b>로 서 있다(V247). 관리자는 게이트 밖이라 지금도 열 수 있고,
      나머지는 환경설정 › 메뉴 관리에서 그 줄을 지우는 날 보인다.
  */
  { path: '/vendors', menuKey: 'vendor_master', label: '협력사 마스터', v1Source: '(v2 신규 · DOC-82)', element: <VendorMasterPage /> },

  // 관리자 대시보드 — 운영 모니터링. 관리자(admin·super_admin)가 연다. 링크는 사이드바가 아니라 상단 헤더에 있다(Navigation).
  // 접근은 백엔드가 역할(ADMIN)로 막는다. 관리자는 라우트 가드에서 이미 전권이라 별도 목록이 필요 없다 —
  // 그 안의 DB 운영 콘솔(/ops)만 최고 관리자·전산 담당으로 남는다(agent/app/ops/auth.py).
  { path: '/admin/dashboard', menuKey: 'admin_dashboard', label: '관리자 대시보드', v1Source: '(v2 신규)', element: <AdminDashboardPage /> },

  // 메뉴에 없는 화면 — 로그인만 확인한다.
  { path: '/mypage', label: '마이페이지', v1Source: 'components/MyPageClient.tsx', element: <MyPage /> },
  // 내 작업 일정(DOC-100) — 홈 TO DO 위젯의 '전체보기' 가 입구다. 마이페이지와 같은 개인 자료라
  // 메뉴 키를 갖지 않는다: 남의 할일을 가리키는 주소 자체가 없고(서버가 로그인 사용자로 가른다),
  // 사이드바에 한 줄을 더하는 것은 이 화면이 매일 쓰이는 것을 본 뒤에 할 일이다.
  { path: '/todos', label: '내 작업 일정', v1Source: '(v2 신규 · DOC-100)', element: <TodoSchedulePage /> },
  // AI Agent 는 메뉴가 아니라 헤더 버튼이라 메뉴 권한을 갖지 않는다. 그래도 키를 붙이는 것은
  // <b>오픈 게이트</b> 때문이다(V153) — 버튼만 감추면 주소를 아는 사람은 그대로 들어간다.
  // 가드는 이 키를 게이트로만 판정하고 권한 검사는 건너뛴다(`GATE_ONLY_MENU_KEYS`).
  { path: '/aiagent', menuKey: 'ai_agent', label: 'AI Agent', v1Source: '(v2 신규)', element: <AiAgentPage /> },
];

/**
 * v1 에서 하위 화면으로 넘기던 경로. Next.js 는 `redirect()` 로 처리했다.
 */
export const SCREEN_REDIRECTS: Array<{ from: string; to: string }> = [
  { from: '/', to: '/home' },
  { from: '/sites', to: '/sites/status' },
  { from: '/news', to: '/news/notices' },
  { from: '/tech', to: '/tech/cases' },
  { from: '/hr', to: '/hr/placement' },
  { from: '/meetings', to: '/meetings/weekly' },
  // 이 자리에 있던 통합 캘린더는 월간업무의 '전체' 탭이 되었다. 그 사이 공유된 링크를 살려 둔다.
  { from: '/calendar', to: '/meetings/monthly' },
  // 주차 관리가 첫 항목이었으나 화면 자체를 없앴다(2026-08-13) — 부서 관리가 그 자리를 받는다.
  { from: '/settings', to: '/settings/departments' },
  /*
   * 도면 뷰어를 자료실이 흡수했다(2026-08-18).
   *
   * 두 화면은 같은 저장소를 같은 컴포넌트로 훑는데 한쪽만 파일을 열 수 있었다 — 사용자에게 그 경계는
   * 설명되지 않는다("자료실에 있는 PDF 를 보려면 도면 뷰어로 가라"). 이제 자료실 목록의 [보기] 가
   * 유일한 입구다.
   *
   * ★ 경로는 살려 둔다. 즐겨찾기·메신저로 공유된 /bim 이 404 가 되면, 사용자에게는 기능이 없어진
   *   것이 아니라 **시스템이 고장 난 것**으로 보인다. 권한 키(bim_viewer)도 지우지 않는다 —
   *   DWG 변환 API 가 그 키를 쓰고, 그랜트를 지우면 되돌릴 때 다시 나눠 줘야 한다.
   */
  { from: '/bim', to: '/docs/s3' },
];
